import { useState } from 'react';
// The package's normal entry, not mammoth/mammoth.browser: that prebuilt bundle
// carries its own frozen copy of JSZip 3.7.1 (pen test AD25996). Vite follows
// mammoth's "browser" field, so this uses the npm jszip instead.
import mammoth from 'mammoth';
import { buildImportOptions, IMPORT_STYLE_MAP } from '../../utils/docxImport';
import { clean } from './HtmlDocumentView';

// Import a Word document as the content of an HTML version.
//
// mammoth converts the .docx in the browser (it is pure JavaScript), which
// keeps the Worker bundle unchanged. The shared helper in utils/docxImport.js
// supplies the style map and, for documents written without Heading styles,
// the bold-paragraph heuristic; when that heuristic was needed the person is
// told, because the headings were guessed and want a look.
//
// The result handed to the parent is what the New Version modal saves:
//   { html, usedHeuristic, wordData: { word_filename, word_content_type, word_data, word_file_size } }
// It is saved with content_kind: 'html' and the Word file attached as the
// audit copy. Nothing is uploaded from here.

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function WordImport({ onReady }) {
  const [state, setState] = useState({ phase: 'idle' }); // idle | converting | ready | error

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) { setState({ phase: 'error', message: 'Choose a .docx file. Older .doc files need saving as .docx in Word first.' }); return; }
    setState({ phase: 'converting', filename: file.name });
    try {
      const arrayBuffer = await file.arrayBuffer();
      let usedHeuristic = false;
      const result = await mammoth.convertToHtml({ arrayBuffer }, {
        styleMap: IMPORT_STYLE_MAP,
        transformDocument: (document) => {
          const opts = buildImportOptions(document);
          usedHeuristic = opts.usedHeuristic;
          return opts.transformDocument ? opts.transformDocument(document) : document;
        },
      });
      const html = result.value || '';
      const words = html.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
      const data = await readAsBase64(file);
      const ready = {
        html,
        usedHeuristic,
        words,
        messages: (result.messages || []).map((m) => m.message),
        // Named as the versions route expects, so the modal spreads it straight into the POST.
        wordData: { word_filename: file.name, word_content_type: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', word_data: data, word_file_size: file.size },
      };
      setState({ phase: 'ready', filename: file.name, ...ready });
      onReady(ready);
    } catch (e) {
      setState({ phase: 'error', message: e.message || 'Could not read that Word file.' });
      onReady(null);
    }
  };

  if (state.phase === 'ready') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
          </svg>
          <span className="text-[9px] font-semibold text-emerald-700 flex-1 truncate">{state.filename}</span>
          <span className="text-[9px] text-emerald-700">{state.words} words</span>
          <button type="button" onClick={() => { setState({ phase: 'idle' }); onReady(null); }} className="text-emerald-400 hover:text-red-500 font-bold text-[11px]">×</button>
        </div>
        {state.usedHeuristic && (
          <div className="text-[9px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">
            This document does not use Word's Heading styles, so the section headings below were worked out from bold paragraphs. Check them in the editor before submitting.
          </div>
        )}
        {state.messages.length > 0 && (
          <div className="text-[9px] text-gray-500 bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg">
            {state.messages.slice(0, 3).map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide">Preview</div>
        <div className="border border-gray-200 rounded-lg bg-white max-h-64 overflow-y-auto px-4 py-3">
          <div className="qms-doc" dangerouslySetInnerHTML={{ __html: clean(state.html) }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className={`block border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${state.phase === 'converting' ? 'border-gold/60 bg-gray-50' : 'border-gray-300 hover:border-gold/60 hover:bg-gray-50'}`}>
        <svg className="w-5 h-5 text-gray-300 mx-auto mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span className="text-[9px] text-gray-400">
          {state.phase === 'converting' ? `Converting ${state.filename}…` : 'Choose the Word document (.docx) to import'}
        </span>
        <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      </label>
      {state.phase === 'error' && <div className="mt-1 text-[8px] text-red-500">{state.message}</div>}
      <div className="mt-1 text-[8px] text-gray-400">Body text, headings, lists, tables and links are converted. Headers, footers and cover pages are not.</div>
    </div>
  );
}
